// frontend/packages/business-console/src/components/content/SeoAssistCard.jsx
//
// Bounded SEO assist: a "suggest a meta title + description from this page"
// helper. It NEVER auto-applies - it shows the suggestion and the operator
// taps Apply per field, which writes into the frontmatter via onApply. Only
// renders when the collection schema actually has a title and/or description
// field (so it doesn't appear on schemas it can't help).
//
// The backend (POST /v2/content/seo-suggestion) does the one capped LLM call,
// strips em-dashes, clamps lengths, and meters the spend. This component is
// pure UI + one fetch.

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import { useTokens, Surface } from '../primitives';
import { suggestSeo } from '../../utils/contentApi';
import { isProseField } from '../../utils/contentEditorHelpers';

function hasField(schemaFields, key) {
  return (
    !!schemaFields && Object.prototype.hasOwnProperty.call(schemaFields, key)
  );
}

function SuggestionRow({ label, value, current, onApply, t }) {
  const isCurrent = String(current || '').trim() === String(value || '').trim();
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '.4px',
          color: t.faint,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: t.text, flexGrow: 1 }}>
          {value || <span style={{ color: t.faint }}>(none suggested)</span>}
        </Typography>
        {value ? (
          <Button
            size="small"
            variant="outlined"
            disabled={isCurrent}
            onClick={() => onApply(value)}
            sx={{ flexShrink: 0, py: 0.125, fontSize: 12, fontWeight: 700 }}
          >
            {isCurrent ? 'Applied' : 'Apply'}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

export default function SeoAssistCard({
  schemaFields,
  frontmatterObj,
  markdownBody,
  onApply,
}) {
  const t = useTokens();
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState('');

  // What we summarize is the whole document's copy, not just the markdown
  // body. Structured pages (a home or services page whose copy lives entirely
  // in frontmatter, with an empty body) used to leave this card permanently
  // disabled behind "add page content first" - on exactly the pages whose
  // search snippet matters most. The backend's own 20-char floor still
  // applies, so a genuinely empty page is still refused.
  //
  // `title` and `description` are held back deliberately: they are the two
  // fields being suggested, and the backend already passes the current title
  // separately as a reference. Feeding them in as source material too just
  // invites the model to hand back what is already there.
  const bodyText = useMemo(() => {
    const parts = [];
    for (const [key, value] of Object.entries(frontmatterObj || {})) {
      if (key === 'title' || key === 'description') continue;
      if (!isProseField(key, value)) continue;
      parts.push(String(value).trim());
    }
    const body = String(markdownBody || '').trim();
    if (body) parts.push(body);
    return parts.join('\n\n').trim();
  }, [frontmatterObj, markdownBody]);

  const showTitle = hasField(schemaFields, 'title');
  const showDescription = hasField(schemaFields, 'description');
  // Every hook above this line: an early return must never sit between the
  // component's first hook and its last.
  if (!showTitle && !showDescription) return null;

  const canSuggest = bodyText.length >= 20 && !loading;

  const run = async () => {
    setLoading(true);
    setError('');
    setSuggestion(null);
    try {
      const result = await suggestSeo({
        body: bodyText,
        title: frontmatterObj?.title || '',
      });
      if (!result) {
        setError(
          "Couldn't generate a suggestion from this page. Add more page content and try again."
        );
      } else {
        setSuggestion(result);
      }
    } catch {
      setError('SEO assist is unavailable right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Surface sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: t.purple }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 900, color: t.text }}>
            SEO assist
          </Typography>
          <Typography sx={{ fontSize: 12, color: t.muted }}>
            Suggest a search title and description from this page. You review
            and apply each one.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          disabled={!canSuggest}
          onClick={run}
          startIcon={
            loading ? (
              <CircularProgress size={14} sx={{ color: 'inherit' }} />
            ) : (
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{ flexShrink: 0, fontWeight: 800 }}
        >
          {loading ? 'Thinking' : suggestion ? 'Redo' : 'Suggest'}
        </Button>
      </Box>

      {!canSuggest && bodyText.length < 20 && !suggestion && !error ? (
        <Typography sx={{ fontSize: 12, color: t.faint, mt: 1 }}>
          Add some content to this page first, then generate a suggestion.
        </Typography>
      ) : null}

      {error ? (
        <Typography sx={{ fontSize: 12.5, color: t.red, mt: 1 }}>
          {error}
        </Typography>
      ) : null}

      {suggestion ? (
        <Box sx={{ mt: 0.5 }}>
          {showTitle ? (
            <SuggestionRow
              label="Suggested title"
              value={suggestion.title}
              current={frontmatterObj?.title}
              onApply={(v) => onApply('title', v)}
              t={t}
            />
          ) : null}
          {showDescription ? (
            <SuggestionRow
              label="Suggested description"
              value={suggestion.description}
              current={frontmatterObj?.description}
              onApply={(v) => onApply('description', v)}
              t={t}
            />
          ) : null}
        </Box>
      ) : null}
    </Surface>
  );
}
